import { Component, Input, inject } from "@angular/core";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { LazyLoadEvent } from "primeng/api";
import { UserService } from "../../_services/user.service";
import { LoggingService, LogLevel } from "../../_services/logging.service";
import { FeatureAccessService } from "../../_services/feature-access.service";
import { paging } from "../../../environments/environment";
import {
  EntityModelLipid,
  LipidCompassQuery,
  LipidControllerService,
  PagedModelEntityModelLipid,
} from "../../../../modules/lipidcompass-backend-client";
import { Pageable } from "../../../../modules/lipidcompass-backend-client/model/pageable";
import { AutoCompleteCompleteEvent, AutoCompleteModule, AutoCompleteSelectEvent, AutoCompleteUnselectEvent } from "primeng/autocomplete";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { GlobalSearchService, SearchResult } from "../../_services/global-search.service";

export type { SearchResult };

@Component({
    selector: "app-main-menu-search",
    templateUrl: "./main-menu-search.component.html",
    styleUrls: ["./main-menu-search.component.scss"],
    standalone: true,
    imports: [
      AutoCompleteModule,
      CommonModule,
      FormsModule,
      RouterModule
    ]
})
export class MainMenuSearchComponent {
  userService = inject(UserService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private loggingService = inject(LoggingService);
  private lipidService = inject(LipidControllerService);
  private searchService = inject(GlobalSearchService);

  pageable: Pageable = { ...paging.defaultPageable };

  results: EntityModelLipid[] = [];
  autoCompleteResults: SearchResult[] = [];
  rowsPerPageOptions: Number[] = [10, 20, 50, 100, 200];

  placeholder: string = "Select Hierarchy Level";

  @Input()
  limit = 100;

  @Input()
  loading: boolean = false;

  @Input()
  disabled: boolean = false;

  @Input()
  selectedLevel: string = "UNDEFINED";

  @Input()
  matchingMode: string = "PREFIX";

  @Input()
  normalizeName: boolean = false;

  selectedResult: SearchResult | null = null;

  query: LipidCompassQuery = <LipidCompassQuery>{
    lipidLevel: null,
    matchMode: "PREFIX",
    mzTabResults: [],
    names: [],
    normalizeName: this.normalizeName,
    selectedFacets: [],
    facets: [],
    unit: null,
    minMass: 0.0,
    maxMass: 10000.0,
    sumFormula: null,
    minValue: 0.0,
    maxValue: Number.MAX_VALUE,
  };

  private featureAccess = inject(FeatureAccessService);

  /**
   * A getter, not a one-shot field initializer: a field would resolve once at
   * construction time and never again, so if `mainmenusearch` were ever gated,
   * an eauser who signs in after the component is created would never see the
   * search box appear (#438). The property name is unchanged so the template
   * binding (`featureEnabled`, no call syntax) needs no edit.
   */
  get featureEnabled(): boolean {
    return this.featureAccess.isEnabled("mainmenusearch");
  }

  constructor() {
    const translate = inject(TranslateService);

    translate.setDefaultLang("en");
    translate.use("en");
  }

  autocompleteSearch(event: AutoCompleteCompleteEvent) {
    this.searchService.search(event.query).subscribe(results => {
      this.autoCompleteResults = results;
    });
  }

  handleSelect(event: AutoCompleteSelectEvent) {
    this.loggingService.log(
      "MainMenuSearchComponent",
      "Received handleSelect on value: " + JSON.stringify(event),
      LogLevel.DEBUG
    );
    this.selectedResult = event.value;
    this.handleSelection();
  }

  handleUnSelect(event: AutoCompleteUnselectEvent) {
    this.loggingService.log(
      "MainMenuSearchComponent",
      "Received handleUnSelect on value: " + JSON.stringify(event),
      LogLevel.DEBUG
    );
    this.selectedResult = null;
  }

  resetState() {
    this.selectedResult = null;
    this.results = [];
    this.autoCompleteResults = [];
    this.disabled = false;
    this.loading = false;
  }

  onKeyUp(event: any) {
    this.loggingService.log(
      "MainMenuSearchComponent",
      "Received onKeyUp event",
      LogLevel.DEBUG
    );
    if (event.keyCode === 13) {
      this.loggingService.log(
        "MainMenuSearchComponent",
        "onKeyUp for ENTER",
        LogLevel.DEBUG
      );
      this.handleSelection();
    }
  }

  handleSelection() {
    if (this.selectedResult) {
      this.disabled = false;
      this.loading = false;
      this.loggingService.log(
        "MainMenuSearchComponent",
        "Selected result: " + JSON.stringify(this.selectedResult),
        LogLevel.DEBUG
      );
      this.router.navigate(this.selectedResult.routerCommands, {relativeTo: this.route}).finally(() => {
        this.resetState();
      });
    }
  }

  loadLipid() {
    this.lipidService
      .lipidFindByLipidQuery(
        this.query,
        this.pageable.page,
        this.pageable.size
      ).subscribe((response: PagedModelEntityModelLipid) => {
        console.debug("Response: " + JSON.stringify(response));
        this.results = response?._embedded?.lipids || [];
      });
  }

  loadQueryPage(event: LazyLoadEvent) {
    this.loading = true;
    let pageIndex = (event.first ?? 0) / (event.rows ?? 10);
    this.loggingService.log(
      "StudyComponent",
      "Page event for page: " + (pageIndex + 1),
      LogLevel.DEBUG
    );
    this.pageable.page = pageIndex;
    this.pageable.size = event.rows;
    this.loggingService.log(
      "MainMenuSearchComponent",
      "Event size before load: " + event.rows,
      LogLevel.DEBUG
    );
    this.loadLipid();
    this.loading = false;
  }
}
